import { FC } from "react";
import { Box, Button, Link, Typography } from "@mui/material";

import { RiLinksLine } from "react-icons/ri";

import { ClaimLink } from "../../../../../types/claims";
import {
  formatDisplayDate,
  formatInstant,
} from "../../../../helpers/dataHelper";
import { claimTag } from "./claimLinks";

/**
 * A claim's link (REV-2629): "Resubmission of #PARENT" on the corrected claim,
 * "Resubmitted as #CHILD" on the original's action. The other claim's number
 * opens it; admins can unlink.
 */
const ResubmissionBanner: FC<{
  link: ClaimLink;
  /** Which side the open claim is. */
  isOriginal: boolean;
  /** The original's correct and resubmit date. */
  actionOn?: string | null;
  onOpenClaim: (claimNumber: string) => void;
  /** Admins only. */
  onUnlink?: () => void;
}> = ({ link, isOriginal, actionOn, onOpenClaim, onUnlink }) => {
  const other = isOriginal ? link.childClaimNumber : link.parentClaimNumber;
  const linked = [
    `Linked ${formatInstant(link.linkedAt)}`,
    link.linkedBy
      ? `by ${link.linkedBy}`
      : link.source === "auto"
        ? "automatically"
        : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box
      role="note"
      aria-label={isOriginal ? "Resubmitted as" : "Resubmission of"}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: "10px",
        border: "1px solid",
        borderColor: "info.light",
        backgroundColor: "#EFF6FF",
      }}
    >
      <Box sx={{ color: "info.main", display: "flex", flexShrink: 0 }}>
        <RiLinksLine size={18} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
          {isOriginal ? "Resubmitted as " : "Resubmission of "}
          <Link
            component="button"
            type="button"
            onClick={() => onOpenClaim(other)}
            sx={{
              fontSize: "inherit",
              fontWeight: "inherit",
              verticalAlign: "baseline",
            }}
          >
            {claimTag(other)}
          </Link>
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
          {[
            actionOn &&
              !isOriginal &&
              `Correct and resubmit of ${formatDisplayDate(actionOn)}`,
            linked,
            isOriginal && `${claimTag(other)}'s remits show on this timeline`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
      </Box>
      {onUnlink && (
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={onUnlink}
          sx={{ flexShrink: 0, textTransform: "none" }}
        >
          Unlink
        </Button>
      )}
    </Box>
  );
};

export default ResubmissionBanner;
